// -- [Role-based rotating headlines - between Pricing and Contact; click to cycle with animation]

import { useState, useEffect } from 'react'
import type { RoleKey } from '@/types/content'
import { ROLE_HEADLINES } from '@/data/roleHeadlines'

// -- [Hero-like gradient: red → orange → yellow; duplicated for 200% width so position shift animates]
const SECTION_GRADIENT =
  'linear-gradient(to right, rgba(255,59,59,0.16), rgba(255,122,59,0.2), rgba(255,188,72,0.16), rgba(255,59,59,0.16), rgba(255,122,59,0.2), rgba(255,188,72,0.16))'

interface RoleHeadlinesSectionProps {
  roleKey: RoleKey
}

export function RoleHeadlinesSection({ roleKey }: RoleHeadlinesSectionProps) {
  const items = ROLE_HEADLINES[roleKey]
  const [index, setIndex] = useState(() =>
    items.length > 0 ? Math.floor(Math.random() * items.length) : 0
  )

  // -- [Reset index when role changes so we don't keep an out-of-bounds index from another role]
  useEffect(() => {
    if (items.length === 0) return
    setIndex((i) => {
      if (i >= items.length) return Math.floor(Math.random() * items.length)
      return i
    })
  }, [roleKey, items.length])

  if (!items.length) return null

  const safeIndex = Math.min(index, items.length - 1)
  const current = items[safeIndex]
  if (!current) return null

  const goNext = () => {
    if (items.length <= 1) return
    window.umami?.track('Role headlines click', {
      headline: current.headline.slice(0, 50),
      index: String(index),
    })
    setIndex((i) => (i + 1) % items.length)
  }

  return (
    <section
      id="role-headlines"
      onClick={goNext}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          goNext()
        }
      }}
      className="group relative py-12 px-6 cursor-pointer select-none rounded-lg border-2 border-brand-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 bg-gray-50 overflow-hidden"
      aria-label="Cycle to next headline"
    >
      {/* -- [Hero-like gradient: instant shift on hover then slow drift] */}
      <div
        className="pointer-events-none absolute inset-0 bg-[length:200%_100%] bg-[position:0%_50%] transition-[background-position] duration-700 group-hover:animate-role-headlines-gradient"
        style={{ backgroundImage: SECTION_GRADIENT }}
        aria-hidden
      />

      {/* -- [Top-right folded corner / flap hint] */}
      <div
        className="absolute right-0 top-0 w-0 h-0 border-[1.25rem] border-transparent border-r-gray-400/70 border-t-gray-400/70 rounded-tr-lg shadow-sm"
        style={{ borderTopRightRadius: 4 }}
        aria-hidden
      />

      <div className="relative max-w-3xl mx-auto text-center">
        <div key={safeIndex} className="animate-headline-enter">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            {current.headline}
          </h2>
          <p className="text-gray-600 text-lg sm:text-xl">{current.subtitle}</p>
        </div>
      </div>
    </section>
  )
}
